import { MUISelect } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useState } from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationPermissions } from "../../../../../../hooks/useApplicationPermissions"
import { digitalManagerApi } from "../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../components/FormPage"
import { ApplicationPermissionGet } from "../../../../../models/ApplicationPermission"
import {
  ApplicationRolePermissionDetailGet,
  ApplicationRolePermissionPost,
} from "../../../../../models/ApplicationRolePermission"
import { Guid } from "../../../../../models/CustomType"

const validationSchema = z.object({
  permissionId: z.string().min(1, "Permission is required"),
})

export function ApplicationRolePermissionCreate() {
  const { applicationId, roleId } = useParams()
  const { applicationPermissions, isLoadingApplicationPermissions } =
    useApplicationPermissions(applicationId)
  const [applicationRolePermissionsIds, setApplicationRolePermissionsIds] = useState<
    Guid[]
  >([])
  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}/roles/${roleId}#permissions`

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<ApplicationRolePermissionPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const permissionIdValue = useWatch({
    control,
    name: "permissionId",
  })

  useEffect(() => {
    reset({
      permissionId: "",
    })
  }, [reset])

  const onSubmit = (values: ApplicationRolePermissionPost) => digitalManagerApi
      .post(
        `/applications/${applicationId}/roles/${roleId}/permissions/${values.permissionId}`,
        {
          permissionId: values.permissionId,
        }
      )
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Permission added to role")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot add permission to role")
      })

  useEffect(() => {
    if (applicationId && roleId) {
      digitalManagerApi
        .get<
          ApplicationRolePermissionDetailGet[]
        >(`/applications/${applicationId}/roles/${roleId}/permissions`)
        .then((res) => {
          if (res.status === StatusCodes.OK && res.data?.length) {
            setApplicationRolePermissionsIds(
              res.data.map(
                (applicationRolePermission: ApplicationRolePermissionDetailGet) =>
                  applicationRolePermission.permission.id
              )
            )
          }
        })
    }
  }, [applicationId, roleId])

  //#region PermissionAutocomplete
  const handleAutocompleteGetOptionLabel = useCallback(
    (option: ApplicationPermissionGet) => option.name,
    []
  )

  const handleAutocompleteGetOptionDisabled = useCallback(
    (option: ApplicationPermissionGet) => {
      const optionAlreadySelected = applicationRolePermissionsIds.find(
        (applicationRolePermissionId: Guid) => option.id === applicationRolePermissionId
      )
      return !!optionAlreadySelected
    },
    [applicationRolePermissionsIds]
  )

  const handlePermissionIdChange = useCallback(
    (next: Array<string | { id: string }>) => {
      const firstItem = next?.[0]
      const selectedId = typeof firstItem === "string" ? firstItem : firstItem?.id
      setValue("permissionId", selectedId ?? "", {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const renderPermissionAutocomplete = useCallback(
    () => (
      <MUISelect
        id="permissionId"
        name="permissionId"
        placeholder="Select permission"
        size={32}
        dropdownLOV={(applicationPermissions ?? []).map((opt) => ({
          id: opt.id,
          name: handleAutocompleteGetOptionLabel(opt) ?? "",
          isDisabled: handleAutocompleteGetOptionDisabled(opt),
        }))}
        enableSearch
        clearable={false}
        isDisabled={isLoadingApplicationPermissions}
        selectedItem={permissionIdValue ? [permissionIdValue] : []}
        onChange={handlePermissionIdChange}
        errors={
          typeof errors.permissionId?.message === "string"
            ? { permissionId: { message: errors.permissionId.message } }
            : undefined
        }
      />
    ),
    [
      applicationPermissions,
      handleAutocompleteGetOptionLabel,
      handleAutocompleteGetOptionDisabled,
      isLoadingApplicationPermissions,
      permissionIdValue,
      handlePermissionIdChange,
      errors.permissionId?.message,
    ]
  )
  //#endregion

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={!!permissionIdValue && isDirty}
      isValid={isValid}
    >
      <FieldRow label="Permission">
        <Controller
          name="permissionId"
          control={control}
          render={renderPermissionAutocomplete}
        />
      </FieldRow>
    </FormPage>
  )
}
